import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { ApiError } from '../../api';
import { eventEndpoints } from '../../api';
import { Button, Panel } from '../../ui';
import type { PanelProps } from '../Event';
import { INPUT } from './shared';

/**
 * Putting this event's picker on another BRE site (D-116).
 *
 * Framing is refused by default and the list here is the only thing that
 * changes that — per event, so the pride picker goes on the pride site and
 * nowhere else. A site that is not listed is refused by the browser itself,
 * which is the only refusal that actually holds; the snippet is not a secret
 * and copying it somewhere else achieves nothing.
 */
export function EmbedPanel({ detail, onError, can }: PanelProps) {
  const eventId = detail.event.id;
  const editable = detail.event.status !== 'ARCHIVED' && can('events.manage');
  const origins = useQuery<{ origins: { id: string; origin: string }[]; snippet_host: string }, ApiError>({
    queryKey: ['event', eventId, 'embed-origins'],
    queryFn: () => eventEndpoints.embedOrigins(eventId),
  });
  const [rows, setRows] = useState<string[]>([]);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (origins.data) setRows(origins.data.origins.map((o) => o.origin));
  }, [origins.data]);

  const save = useMutation({
    mutationFn: () => eventEndpoints.setEmbedOrigins(eventId, rows.map((r) => r.trim()).filter(Boolean)),
    onSuccess: () => void origins.refetch(),
    onError,
  });

  const host = origins.data?.snippet_host ?? '';
  const snippet = `<script src="${host}/embed.js" data-event="${detail.event.slug}"></script>`;

  return (
    <div className="space-y-4">
      <Panel
        title="Websites that may embed this event"
        description="The picker can sit inside another of your sites instead of sending people here. Only the sites listed below are allowed to frame it; every other site is refused by the browser."
        actions={editable ? <Button variant="primary" disabled={save.isPending} onClick={() => save.mutate()}>{save.isPending ? 'Saving…' : 'Save'}</Button> : null}
      >
        <div className="space-y-3 px-5 py-4">
          {rows.length === 0 ? <p className="text-sm text-ink-400">No site may embed this event yet.</p> : null}
          {rows.map((origin, index) => (
            <div key={index} className="flex items-center gap-2">
              <input
                value={origin}
                disabled={!editable}
                placeholder="https://btownpride.com"
                onChange={(event) => setRows(rows.map((r, i) => (i === index ? event.target.value : r)))}
                className={`${INPUT} flex-1`}
                aria-label={`Allowed website ${index + 1}`}
              />
              {editable ? (
                <Button variant="danger" onClick={() => setRows(rows.filter((_, i) => i !== index))}>
                  Remove
                </Button>
              ) : null}
            </div>
          ))}
          {editable ? <Button onClick={() => setRows([...rows, ''])}>Add a website</Button> : null}
          <p className="text-xs text-ink-400">
            Use the whole address and nothing after it — <code>https://btownpride.com</code>, not <code>btownpride.com/tickets</code>. A browser treats{' '}
            <code>btownpride.com</code> and <code>www.btownpride.com</code> as two different sites, so list both if the site answers on both.
          </p>
        </div>
      </Panel>

      <Panel title="The snippet" description="Paste this where the picker should appear. It sizes itself to its contents.">
        <div className="space-y-3 px-5 py-4">
          <pre className="overflow-x-auto rounded border border-line bg-page p-3 font-mono text-xs text-ink-700">{snippet}</pre>
          <Button
            onClick={() => {
              void navigator.clipboard.writeText(snippet).then(
                () => {
                  setCopied(true);
                  window.setTimeout(() => setCopied(false), 2000);
                },
                () => onError({ message: 'Could not copy; select the text instead' } as ApiError),
              );
            }}
          >
            {copied ? 'Copied' : 'Copy snippet'}
          </Button>
          <p className="text-xs text-ink-400">
            Choosing seats happens in the frame. Checkout opens a new tab on {host.replace(/^https?:\/\//, '')} — a card form and a sign-in belong on the site that
            owns them, and the held seats travel with the customer.
          </p>
        </div>
      </Panel>
    </div>
  );
}
